import { noop, isNotEmpty } from "neetocist";
import { Tooltip } from "neetoui";
import { BlockNavigation, Switch } from "neetoui/formik";
import PropTypes from "prop-types";
import { isEmpty, pluck } from "ramda";

import { EmailFormProvider, EmailForm, EmailPreview } from "..";
import Header from "../../Header";
import MobilePreviewHeader from "../../MobilePreviewHeader";
import { INITIAL_FORM_VALUES } from "../constants";
import "../email-form.scss";
import { resolveContent } from "../utils";

const EmailFormComponent = ({
  onSubmit = noop,
  initialValues = INITIAL_FORM_VALUES,
  children,
  formikProps,
  validationSchema,
  showSendToField = false,
  showReplyToField = false,
  showSendToAsRadio = false,
  blockNavigation = true,
  showCcField = false,
  showBccField = false,
  isLoading = false,
  maxEmails = 10,
  emailFormProps = {},
  emailPreviewProps = {},
  headerProps = {},
  tooltipProps = {},
  switchProps = {},
  mobilePreviewProps = {},
}) => {
  const { formatBody, formatSubject, bodyVariables, subjectVariables } =
    emailPreviewProps;

  const shouldShowFields = values => isEmpty(switchProps) || values.isEnabled;

  return (
    <EmailFormProvider
      {...{
        formikProps,
        initialValues,
        maxEmails,
        onSubmit,
        showBccField,
        showCcField,
        showReplyToField,
        showSendToAsRadio,
        showSendToField,
        validationSchema,
      }}
    >
      {({ values, isSubmitting }) => (
        <div className="neeto-notification-email-wrapper flex flex-grow flex-col overflow-y-auto lg:flex-row">
          <div className="neeto-notification-email-wrapper__form-wrapper flex w-full flex-col px-5 md:px-10 lg:h-full lg:w-1/2 lg:overflow-y-auto">
            {blockNavigation && <BlockNavigation />}
            {isNotEmpty(headerProps) && (
              <Header className="px-0" size="small" {...headerProps} />
            )}
            {isNotEmpty(switchProps) && (
              <div className="flex items-center justify-between gap-y-0.5 pb-6">
                <Tooltip
                  disabled={!switchProps?.disabled}
                  position="top"
                  {...tooltipProps}
                >
                  <div className="flex items-center gap-2">
                    <Switch
                      data-testid="switch"
                      name="isEnabled"
                      {...switchProps}
                    />
                  </div>
                </Tooltip>
              </div>
            )}
            {shouldShowFields(values) && children}
            <EmailForm
              isLoading={isSubmitting || isLoading}
              isUpdating={isSubmitting || isLoading}
              showFields={shouldShowFields(values)}
              {...emailFormProps}
            />
          </div>
          {shouldShowFields(values) && (
            <MobilePreviewHeader
              className="lg:hidden"
              {...mobilePreviewProps}
            />
          )}
          {shouldShowFields(values) && (
            <div className="neeto-notification-email-wrapper__preview-wrapper neeto-ui-bg-gray-100 w-full px-6 py-10 lg:w-1/2 lg:overflow-y-auto">
              <EmailPreview
                body={resolveContent(values.message, formatBody, bodyVariables)}
                from={values.sendFrom}
                to={pluck("value", values?.sendTo ?? [])}
                subject={resolveContent(
                  values.subject,
                  formatSubject,
                  subjectVariables
                )}
                {...emailPreviewProps}
              />
            </div>
          )}
        </div>
      )}
    </EmailFormProvider>
  );
};

EmailFormComponent.propTypes = {
  /**
   * A function that will be triggered with input values when the 'Save changes' button is clicked.
   */
  onSubmit: PropTypes.func,
  /**
   * These are the predefined values that populate the form fields when the form is first rendered.
   */
  initialValues: PropTypes.shape({
    sendTo: PropTypes.array,
    sendToId: PropTypes.string,
    sendToCc: PropTypes.array,
    sendToBcc: PropTypes.array,
    replyTo: PropTypes.string,
    showCopyEmails: PropTypes.bool,
    subject: PropTypes.string,
    message: PropTypes.string,
    isEnabled: PropTypes.bool,
  }),
  /**
   * A prop that can be used to override the default Formik properties that are set within the component, providing customization flexibility.
   */
  formikProps: PropTypes.object,
  /**
   * A Yup object defining custom form validation rules, which will be merged with the default rules using the concat method.
   */
  validationSchema: PropTypes.object,
  /**
   * A prop used for rendering either standard React element or a render prop function.
   */
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  /**
   * A Boolean value that determines whether the '**sendTo**' field should be displayed or not.
   */
  showSendToField: PropTypes.bool,
  /**
   * A Boolean value that determines whether the '**replyTo**' field should be displayed or not.
   */
  showReplyToField: PropTypes.bool,
  /**
   * A Boolean value that determines whether the '**sendTo**' field should be radio type or not.
   */
  showSendToAsRadio: PropTypes.bool,
  /**
   * A Boolean value that determines whether the '**Cc**' field should be displayed or not when showSendToField is false.
   * This prop has no effect when showSendToField is true.
   */
  showCcField: PropTypes.bool,
  /**
   * A Boolean value that determines whether the '**Bcc**' field should be displayed or not when showSendToField is false.
   * This prop has no effect when showSendToField is true.
   */
  showBccField: PropTypes.bool,
  /**
   * This Boolean value, when set to true, triggers the display of a loading indicator on the 'Save changes' button."
   */
  isLoading: PropTypes.bool,
  /**
   * A prop to limit number of send to emails
   */
  maxEmails: PropTypes.number,
  /**
   * These are the values that will be passed to EmailForm Component
   */
  emailFormProps: PropTypes.shape({
    messageVariables: PropTypes.array,
    subjectVariables: PropTypes.array,
    handleCancel: PropTypes.func,
    handleResetToDefault: PropTypes.func,
    isLoading: PropTypes.bool,
    replyToFieldProps: PropTypes.object,
    sendToFieldProps: PropTypes.object,
    replyToOptions: PropTypes.array,
    sendToOptions: PropTypes.array,
    isUpdating: PropTypes.bool,
    showFields: PropTypes.bool,
    className: PropTypes.string,
    showResetToDefaultButton: PropTypes.bool,
    editorRef: PropTypes.any,
    editorAddons: PropTypes.array,
  }),
  /**
   * These are the values that will be passed to EmailPreview Component
   */
  emailPreviewProps: PropTypes.shape({
    to: PropTypes.array,
    from: PropTypes.string,
    subject: PropTypes.string,
    body: PropTypes.string,
    actionButtonText: PropTypes.string,
    logo: PropTypes.string,
    customContent: PropTypes.string,
    className: PropTypes.string,
    formatBody: PropTypes.func,
    formatSubject: PropTypes.func,
    bodyVariables: PropTypes.object,
    subjectVariables: PropTypes.object,
  }),
  /**
   * These are the values that will be passed to Header Component
   */
  headerProps: PropTypes.object,
  /**
   * These are the values that will be passed to Tooltip Component
   */
  tooltipProps: PropTypes.object,
  /**
   * These are the values that will be passed to Switch Component
   */
  switchProps: PropTypes.object,
  /**
   * These are the values that will be passed to MobilePreviewHeader Component
   */
  mobilePreviewProps: PropTypes.object,
};

export default EmailFormComponent;
